import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import {
    getDatabase, ref, onValue, set, get, push, update, off
} from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

// ─── Firebase ─────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}
function getFirebaseAuth() { getDB(); return _auth }

// ─── Bot API ──────────────────────────────────────────────────────────────────
const BOT_API = 'http://localhost:3001'
async function notifyUser(uid, message) {
    try {
        await fetch(`${BOT_API}/api/notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, message })
        })
    } catch (e) { console.warn('notify failed:', e.message) }
}

// ─── Constants ────────────────────────────────────────────────────────────────
const DISPUTE_DEPOSIT   = 10
const H2H_EVIDENCE_MS   = 3  * 60 * 1000   // 3 min
const TOURN_EVIDENCE_MS = 20 * 60 * 1000   // 20 min

// Valid evidence URL patterns
const VALID_EVIDENCE_PATTERNS = [
    /^https?:\/\/(www\.)?youtube\.com\/watch\?v=/,
    /^https?:\/\/youtu\.be\//,
    /^https?:\/\/drive\.google\.com\//,
    /^https?:\/\/docs\.google\.com\//,
]

function isValidEvidenceUrl(url) {
    return VALID_EVIDENCE_PATTERNS.some(p => p.test(url.trim()))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(ms) {
    if (ms <= 0) return '0:00'
    const s = Math.floor(ms / 1000)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ─── Stage config ─────────────────────────────────────────────────────────────
const STAGES = [
    { key: 'open',      label: 'Dispute Open' },
    { key: 'evidence',  label: 'Evidence Window' },
    { key: 'submitted', label: 'Evidence Submitted' },
    { key: 'review',    label: 'Under Review' },
    { key: 'resolved',  label: 'Resolved' },
]

function stageIndex(status) {
    const map = {
        open:      0,
        evidence:  1,
        submitted: 2,
        review:    3,
        resolved:  4,
        refunded:  4,
    }
    return map[status] ?? 0
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function DisputePage() {
    const { did }  = useParams()   // dispute ID
    const navigate = useNavigate()

    const [currentUser, setCurrentUser] = useState(null)
    const [authReady, setAuthReady]     = useState(false)
    const [dispute, setDispute]         = useState(null)
    const [loading, setLoading]         = useState(true)
    const [error, setError]             = useState('')

    // Evidence form
    const [evidenceUrl, setEvidenceUrl]     = useState('')
    const [evidenceError, setEvidenceError] = useState('')
    const [submitting, setSubmitting]       = useState(false)
    const [evidenceSubmitted, setEvidenceSubmitted] = useState(false)

    // Timer
    const [timeLeft, setTimeLeft]   = useState(null)
    const timerRef                  = useRef(null)

    // ── Auth ──────────────────────────────────────────────────
    useEffect(() => {
        const auth = getFirebaseAuth()
        const unsub = onAuthStateChanged(auth, user => {
            setCurrentUser(user)
            setAuthReady(true)
        })
        return () => unsub()
    }, [])

    // ── Listen to dispute ─────────────────────────────────────
    useEffect(() => {
        if (!did) return
        const db = getDB()
        const dRef = ref(db, `disputes/${did}`)
        const unsub = onValue(dRef, snap => {
            if (!snap.exists()) { setError('Dispute not found.'); setLoading(false); return }
            const data = snap.val()
            setDispute(data)
            setLoading(false)

            // Check if current user already submitted evidence
            if (currentUser && data.evidence?.[currentUser.uid]) {
                setEvidenceSubmitted(true)
            }
        })
        return () => off(dRef)
    }, [did, currentUser])

    // ── Evidence countdown ────────────────────────────────────
    useEffect(() => {
        if (!dispute || !['open', 'evidence'].includes(dispute.status)) {
            clearInterval(timerRef.current)
            return
        }
        const evidenceDeadline = dispute.evidenceDeadline
        if (!evidenceDeadline) return

        const tick = () => {
            const left = evidenceDeadline - Date.now()
            setTimeLeft(Math.max(0, left))
            if (left <= 0) {
                clearInterval(timerRef.current)
                handleEvidenceWindowClose()
            }
        }
        tick()
        timerRef.current = setInterval(tick, 1000)
        return () => clearInterval(timerRef.current)
    }, [dispute?.evidenceDeadline, dispute?.status])

    // ── Close evidence window → move to review ────────────────
    const handleEvidenceWindowClose = useCallback(async () => {
        if (!dispute || dispute.status !== 'evidence') return
        const db = getDB()
        await update(ref(db, `disputes/${did}`), {
            status: 'review',
            reviewStartedAt: Date.now(),
        })
        // Notify all parties
        const parties = dispute.parties || []
        for (const uid of parties) {
            await notifyUser(uid,
                `🔍 *Dispute Under Review*\n\n` +
                `The evidence window has closed for your dispute.\n` +
                `Our team is now reviewing the case.\n\n` +
                `⏳ Resolution may take up to *24 hours*.\n` +
                `📹 If you did not submit evidence, this may affect the decision.`
            )
        }
    }, [dispute, did])

    // ─────────────────────────────────────────────────────────
    //  DERIVED
    // ─────────────────────────────────────────────────────────
    const isParty = currentUser && dispute?.parties?.includes(currentUser.uid)
    const evidenceCount = dispute?.evidence ? Object.keys(dispute.evidence).length : 0
    const totalParties  = dispute?.parties?.length ?? 0
    const myEvidence    = currentUser && dispute?.evidence?.[currentUser.uid]
    const evidenceWindowOpen = dispute?.status === 'evidence' && timeLeft > 0

    // ─────────────────────────────────────────────────────────
    //  SUBMIT EVIDENCE
    // ─────────────────────────────────────────────────────────
    const handleSubmitEvidence = useCallback(async () => {
        setEvidenceError('')
        if (!evidenceUrl.trim()) { setEvidenceError('Please enter a URL.'); return }
        if (!isValidEvidenceUrl(evidenceUrl)) {
            setEvidenceError('Only YouTube or Google Drive links are accepted.')
            return
        }
        setSubmitting(true)
        try {
            const db = getDB()
            await update(ref(db, `disputes/${did}/evidence/${currentUser.uid}`), {
                url:         evidenceUrl.trim(),
                submittedAt: Date.now(),
                displayName: dispute?.partyNames?.[currentUser.uid] || 'Player',
            })

            // Check if all parties submitted
            const updatedSnap = await get(ref(db, `disputes/${did}/evidence`))
            const submittedCount = updatedSnap.exists() ? Object.keys(updatedSnap.val()).length : 0

            if (submittedCount >= totalParties) {
                await update(ref(db, `disputes/${did}`), { status: 'submitted' })
            }

            // Notify other parties
            const others = (dispute?.parties || []).filter(uid => uid !== currentUser.uid)
            for (const uid of others) {
                await notifyUser(uid,
                    `📹 *Evidence Submitted*\n\n` +
                    `${dispute?.partyNames?.[currentUser.uid] || 'A player'} has submitted their evidence.\n` +
                    `Please submit yours before the window closes.`
                )
            }

            setEvidenceSubmitted(true)
        } catch (e) {
            setEvidenceError('Failed to submit. Try again.')
        }
        setSubmitting(false)
    }, [evidenceUrl, did, currentUser, dispute, totalParties])

    // ─────────────────────────────────────────────────────────
    //  RENDER HELPERS
    // ─────────────────────────────────────────────────────────
    const StatusBar = () => {
        const current = stageIndex(dispute?.status)
        return (
            <div style={s.statusBar}>
                {STAGES.map((stage, i) => {
                    const done   = i < current
                    const active = i === current
                    return (
                        <React.Fragment key={stage.key}>
                            <div style={s.stageWrap}>
                                <div style={
                                    done   ? s.dotDone :
                                    active ? s.dotActive :
                                             s.dotLocked
                                }>
                                    {done ? '✓' : active ? '' : '🔒'}
                                </div>
                                <div style={done || active ? s.stageLabel : s.stageLabelDim}>
                                    {stage.label}
                                </div>
                            </div>
                            {i < STAGES.length - 1 && (
                                <div style={i < current ? s.connDone : s.connPending} />
                            )}
                        </React.Fragment>
                    )
                })}
            </div>
        )
    }

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    if (!authReady || loading) {
        return (
            <div style={s.root}>
                <div style={s.centered}>
                    <div style={s.spinner} />
                    <div style={s.loadText}>Loading dispute…</div>
                </div>
            </div>
        )
    }

    if (error || !dispute) {
        return (
            <div style={s.root}>
                <div style={s.centered}>
                    <div style={{ fontSize: 48 }}>⚠️</div>
                    <div style={s.errorBig}>{error || 'Dispute not found'}</div>
                    <button style={s.backBtn} onClick={() => navigate('/')}>← Home</button>
                </div>
            </div>
        )
    }

    if (!isParty && authReady) {
        return (
            <div style={s.root}>
                <div style={s.centered}>
                    <div style={{ fontSize: 48 }}>⛔</div>
                    <div style={s.errorBig}>You're not part of this dispute.</div>
                    <button style={s.backBtn} onClick={() => navigate('/')}>← Home</button>
                </div>
            </div>
        )
    }

    // ── RESOLVED ──────────────────────────────────────────────
    if (dispute.status === 'resolved' || dispute.status === 'refunded') {
        const iWon = dispute.winner === currentUser?.uid
        const wasPenalised = dispute.penalisedUid === currentUser?.uid
        return (
            <div style={s.root}>
                <div style={s.page}>
                    <div style={s.header}>
                        <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                        <span style={s.headerTitle}>Dispute Resolved</span>
                    </div>
                    <div style={s.card}>
                        <div style={{ fontSize: 56, textAlign: 'center', marginBottom: 12 }}>
                            {dispute.status === 'refunded' ? '💸' : iWon ? '🏆' : '💔'}
                        </div>
                        <div style={{ ...s.bigLabel, color: dispute.status === 'refunded' ? '#fbbf24' : iWon ? '#4ade80' : '#f87171' }}>
                            {dispute.status === 'refunded'
                                ? 'Both Players Refunded'
                                : iWon
                                    ? 'Dispute Won'
                                    : 'Dispute Lost'}
                        </div>
                        <div style={s.subLabel}>
                            {dispute.adminNote || 'Admin has reviewed and resolved this dispute.'}
                        </div>
                        {wasPenalised && (
                            <div style={s.penaltyBox}>
                                ⚠️ A <strong>-10 💎 penalty</strong> has been applied to your account for submitting an incorrect result.
                            </div>
                        )}
                        {dispute.status === 'resolved' && (
                            <div style={s.resolvedBox}>
                                <div style={s.resolvedRow}>
                                    <span>Decision</span>
                                    <span style={{ color: '#4ade80', fontWeight: 700 }}>
                                        {dispute.winnerName || 'See your balance'} won
                                    </span>
                                </div>
                                <div style={s.resolvedRow}>
                                    <span>Resolved at</span>
                                    <span>{dispute.resolvedAt ? new Date(dispute.resolvedAt).toLocaleString() : '—'}</span>
                                </div>
                            </div>
                        )}
                        <button style={s.greenBtn} onClick={() => navigate('/')}>Back to Home</button>
                    </div>
                </div>
            </div>
        )
    }

    // ── UNDER REVIEW ──────────────────────────────────────────
    if (dispute.status === 'review' || dispute.status === 'submitted') {
        return (
            <div style={s.root}>
                <div style={s.page}>
                    <div style={s.header}>
                        <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                        <span style={s.headerTitle}>Under Review</span>
                    </div>
                    <div style={s.card}><StatusBar /></div>
                    <div style={s.card}>
                        <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>🔍</div>
                        <div style={s.bigLabel}>Under Admin Review</div>
                        <div style={s.subLabel}>
                            Our team is reviewing the evidence. This may take up to <strong>24 hours</strong>.
                        </div>
                        <div style={s.evidenceSummary}>
                            <div style={s.evidenceCountBadge}>
                                📹 {evidenceCount} of {totalParties} evidence submitted
                            </div>
                            {Object.entries(dispute.evidence || {}).map(([uid, ev]) => (
                                <div key={uid} style={s.evidenceRow}>
                                    <div style={s.evidenceAvatar}>{initials(ev.displayName)}</div>
                                    <div style={s.evidenceName}>{ev.displayName}</div>
                                    <div style={s.evidenceStatus}>✅ Submitted</div>
                                </div>
                            ))}
                            {(dispute.parties || []).filter(uid => !dispute.evidence?.[uid]).map(uid => (
                                <div key={uid} style={s.evidenceRow}>
                                    <div style={{ ...s.evidenceAvatar, background: '#2a0a0a' }}>
                                        {initials(dispute.partyNames?.[uid] || '?')}
                                    </div>
                                    <div style={s.evidenceName}>{dispute.partyNames?.[uid] || 'Player'}</div>
                                    <div style={{ ...s.evidenceStatus, color: '#f87171' }}>❌ Not submitted</div>
                                </div>
                            ))}
                        </div>
                        <div style={s.warningBox}>
                            ⚠️ Players who did not submit evidence may not be favoured in the decision.
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ── OPEN / EVIDENCE WINDOW ────────────────────────────────
    return (
        <div style={s.root}>
            <div style={s.page}>

                {/* Header */}
                <div style={s.header}>
                    <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                    <span style={s.headerTitle}>Dispute</span>
                    <span style={s.disputeTypeBadge}>
                        {dispute.type === 'h2h' ? '⚔️ H2H' : '🏆 Tournament'}
                    </span>
                </div>

                {/* Status bar */}
                <div style={s.card}><StatusBar /></div>

                {/* Dispute info */}
                <div style={s.infoCard}>
                    <div style={s.infoTitle}>
                        {dispute.type === 'h2h' ? '⚔️ H2H Dispute' : '🏆 Tournament Dispute'}
                    </div>
                    <div style={s.infoSub}>
                        {dispute.gameName || (dispute.gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️')}
                    </div>
                    <div style={s.infoRow}>
                        <span style={s.infoLabel}>Stake / Prize</span>
                        <span style={s.infoVal}>💎 {dispute.stake || dispute.prizePool || '—'}</span>
                    </div>
                    <div style={s.infoRow}>
                        <span style={s.infoLabel}>Dispute type</span>
                        <span style={s.infoVal}>
                            {dispute.autoDispute ? '⚡ Auto (mismatched claims)' : '🔴 Manual dispute'}
                        </span>
                    </div>
                    {dispute.depositPaid && (
                        <div style={s.infoRow}>
                            <span style={s.infoLabel}>Deposit paid</span>
                            <span style={{ ...s.infoVal, color: '#fbbf24' }}>💎 10 (refunded if you win)</span>
                        </div>
                    )}
                </div>

                {/* Claims summary */}
                <div style={s.card}>
                    <div style={s.sectionTitle}>📊 Conflicting Claims</div>
                    {Object.entries(dispute.claims || {}).map(([uid, claim]) => (
                        <div key={uid} style={s.claimRow}>
                            <div style={s.claimAvatar}>{initials(dispute.partyNames?.[uid] || '?')}</div>
                            <div style={s.claimInfo}>
                                <div style={s.claimName}>
                                    {dispute.partyNames?.[uid] || 'Player'}
                                    {uid === currentUser?.uid && <span style={s.youTag}> (you)</span>}
                                </div>
                                <div style={s.claimVal}>Claimed: <strong>{claim}</strong></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Evidence window */}
                <div style={s.card}>
                    <div style={s.sectionTitle}>📹 Evidence Submission</div>

                    {/* Timer */}
                    {evidenceWindowOpen && (
                        <div style={s.timerCard}>
                            <div style={s.timerLabel}>Evidence window closes in</div>
                            <div style={{
                                ...s.timerValue,
                                color: timeLeft < 60000 ? '#f87171' : '#fbbf24'
                            }}>{fmtTime(timeLeft)}</div>
                            <div style={s.timerSub}>
                                Submit your YouTube or Google Drive recording link before time runs out.
                                If you don't submit, the decision may not favour you.
                            </div>
                        </div>
                    )}

                    {timeLeft === 0 && !evidenceSubmitted && (
                        <div style={s.expiredBox}>
                            ⏰ Evidence window has closed. You did not submit evidence.
                            This may affect the decision against you.
                        </div>
                    )}

                    {/* Evidence count */}
                    <div style={s.evidenceCountRow}>
                        <span style={s.evidenceCountBadge}>
                            📹 {evidenceCount}/{totalParties} submitted
                        </span>
                    </div>

                    {/* Party evidence status */}
                    {(dispute.parties || []).map(uid => {
                        const ev = dispute.evidence?.[uid]
                        const isMe = uid === currentUser?.uid
                        return (
                            <div key={uid} style={s.evidenceRow}>
                                <div style={{
                                    ...s.evidenceAvatar,
                                    background: ev ? '#0a2a0c' : '#1a0a0a',
                                    border: ev ? '1px solid #2a5a2c' : '1px solid #3a1a1a',
                                }}>
                                    {initials(dispute.partyNames?.[uid] || '?')}
                                </div>
                                <div style={s.evidenceName}>
                                    {dispute.partyNames?.[uid] || 'Player'}
                                    {isMe && <span style={s.youTag}> (you)</span>}
                                </div>
                                <div style={ev ? s.evidenceSubmittedTag : s.evidencePendingTag}>
                                    {ev ? '✅ Submitted' : '⏳ Pending'}
                                </div>
                            </div>
                        )
                    })}

                    {/* Evidence form for current user */}
                    {isParty && !evidenceSubmitted && evidenceWindowOpen && (
                        <div style={s.evidenceForm}>
                            <div style={s.evidenceFormTitle}>Submit Your Recording</div>
                            <div style={s.evidenceFormNote}>
                                Only YouTube or Google Drive links accepted.
                            </div>
                            <input
                                style={s.input}
                                placeholder="https://youtube.com/watch?v=... or https://drive.google.com/..."
                                value={evidenceUrl}
                                onChange={e => { setEvidenceUrl(e.target.value); setEvidenceError('') }}
                            />
                            {evidenceError && (
                                <div style={s.evidenceErrorMsg}>{evidenceError}</div>
                            )}
                            <button
                                style={s.submitEvidenceBtn}
                                onClick={handleSubmitEvidence}
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting…' : '📹 Submit Evidence'}
                            </button>
                        </div>
                    )}

                    {evidenceSubmitted && myEvidence && (
                        <div style={s.submittedBox}>
                            <div style={s.submittedTitle}>✅ Your evidence submitted</div>
                            <a
                                href={myEvidence.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={s.evidenceLink}
                            >
                                {myEvidence.url.length > 50
                                    ? myEvidence.url.slice(0, 50) + '…'
                                    : myEvidence.url}
                            </a>
                        </div>
                    )}
                </div>

                {/* Warning */}
                <div style={s.warningCard}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <div>
                        <div style={s.warningTitle}>Important</div>
                        <div style={s.warningBody}>
                            Players found to have submitted false results will be penalised
                            <strong> -10 💎</strong> deducted from their balance.
                            {dispute.autoDispute
                                ? ' This dispute was raised automatically due to mismatched claims.'
                                : ' The player who raised this dispute paid a 10 💎 deposit — refunded if their claim is correct.'}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#08060f', color: '#e8e0ff', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    page: { maxWidth: 480, margin: '0 auto', padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 },
    centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, padding: 24, textAlign: 'center' },

    header: { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0 8px' },
    backIcon: { background: 'none', border: 'none', color: '#a78bfa', fontSize: 22, cursor: 'pointer', padding: '4px 8px' },
    headerTitle: { fontSize: 18, fontWeight: 700, color: '#e8e0ff', flex: 1 },
    disputeTypeBadge: { fontSize: 12, fontWeight: 700, color: '#a78bfa', background: '#1a0a3a', border: '1px solid #4c1d95', padding: '4px 10px', borderRadius: 20 },

    card: { background: '#0f0a1a', border: '1px solid #2a1a4a', borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 12 },

    // Info card
    infoCard: { background: 'linear-gradient(135deg, #12082a, #1a0a3a)', border: '1px solid #4c1d95', borderRadius: 14, padding: '18px 16px' },
    infoTitle: { fontSize: 17, fontWeight: 800, color: '#e8e0ff', marginBottom: 4 },
    infoSub: { fontSize: 13, color: '#7c6fad', marginBottom: 12 },
    infoRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1a0a2a' },
    infoLabel: { fontSize: 13, color: '#7c6fad' },
    infoVal: { fontSize: 13, fontWeight: 600, color: '#e8e0ff' },

    // Status bar
    statusBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    stageWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 },
    dotDone: { width: 24, height: 24, borderRadius: '50%', background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 },
    dotActive: { width: 24, height: 24, borderRadius: '50%', background: '#1a0a3a', border: '2px solid #7c3aed', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 },
    dotLocked: { width: 24, height: 24, borderRadius: '50%', background: '#0a060f', border: '1px solid #2a1a4a', color: '#3a2a5a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 },
    stageLabel: { fontSize: 8, color: '#a78bfa', textAlign: 'center', letterSpacing: '0.03em' },
    stageLabelDim: { fontSize: 8, color: '#3a2a5a', textAlign: 'center' },
    connDone: { flex: 0.2, height: 2, background: '#7c3aed', borderRadius: 1, marginBottom: 14 },
    connPending: { flex: 0.2, height: 2, background: '#2a1a4a', borderRadius: 1, marginBottom: 14 },

    // Claims
    sectionTitle: { fontSize: 13, fontWeight: 700, color: '#a78bfa' },
    claimRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1a0a2a' },
    claimAvatar: { width: 36, height: 36, borderRadius: '50%', background: '#2a1a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: '#a78bfa', flexShrink: 0 },
    claimInfo: { flex: 1 },
    claimName: { fontSize: 13, fontWeight: 600, color: '#e8e0ff' },
    claimVal: { fontSize: 12, color: '#7c6fad', marginTop: 2 },
    youTag: { fontSize: 10, color: '#a78bfa', fontWeight: 600 },

    // Evidence
    timerCard: { background: '#1a0a2a', border: '1px solid #4c1d95', borderRadius: 10, padding: '14px 16px', textAlign: 'center' },
    timerLabel: { fontSize: 11, color: '#7c6fad', marginBottom: 6, letterSpacing: '0.06em' },
    timerValue: { fontSize: 36, fontWeight: 900, fontVariantNumeric: 'tabular-nums', marginBottom: 6 },
    timerSub: { fontSize: 12, color: '#5a4a7a', lineHeight: 1.5 },

    evidenceCountRow: { display: 'flex', justifyContent: 'flex-end' },
    evidenceCountBadge: { fontSize: 12, fontWeight: 700, color: '#a78bfa', background: '#1a0a3a', padding: '4px 12px', borderRadius: 20, border: '1px solid #4c1d95' },
    evidenceSummary: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 },
    evidenceRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #0f0620' },
    evidenceAvatar: { width: 32, height: 32, borderRadius: '50%', background: '#1a0a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#a78bfa', flexShrink: 0 },
    evidenceName: { flex: 1, fontSize: 13, color: '#e8e0ff' },
    evidenceStatus: { fontSize: 12, color: '#4ade80' },
    evidenceSubmittedTag: { fontSize: 11, fontWeight: 700, color: '#4ade80', background: '#0a2a0c', padding: '2px 10px', borderRadius: 10 },
    evidencePendingTag: { fontSize: 11, color: '#fbbf24', background: '#2a1a00', padding: '2px 10px', borderRadius: 10 },

    evidenceForm: { background: '#0a060f', border: '1px dashed #4c1d95', borderRadius: 10, padding: '14px', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 },
    evidenceFormTitle: { fontSize: 13, fontWeight: 700, color: '#e8e0ff' },
    evidenceFormNote: { fontSize: 12, color: '#5a4a7a' },
    input: { padding: '11px 14px', borderRadius: 10, border: '1px solid #2a1a4a', background: '#0a060f', color: '#e8e0ff', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    evidenceErrorMsg: { fontSize: 12, color: '#f87171', background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: 8, padding: '8px 12px' },
    submitEvidenceBtn: { padding: '12px', borderRadius: 10, background: '#7c3aed', border: 'none', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', textAlign: 'center' },

    submittedBox: { background: '#0a1a0c', border: '1px solid #2a5a2c', borderRadius: 10, padding: '12px 14px' },
    submittedTitle: { fontSize: 13, fontWeight: 700, color: '#4ade80', marginBottom: 4 },
    evidenceLink: { fontSize: 12, color: '#a78bfa', textDecoration: 'underline', wordBreak: 'break-all' },

    expiredBox: { background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#f87171' },

    // Warning
    warningCard: { background: '#1a0a00', border: '1px solid #78350f', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' },
    warningTitle: { fontSize: 13, fontWeight: 700, color: '#fbbf24', marginBottom: 4 },
    warningBody: { fontSize: 12, color: '#92400e', lineHeight: 1.5 },

    // Resolved
    resolvedBox: { background: '#0a0f0a', border: '1px solid #1a3a1c', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
    resolvedRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#4a7a50' },
    penaltyBox: { background: '#2a0a0a', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#f87171', marginBottom: 12 },

    // Buttons
    greenBtn: { padding: '13px', borderRadius: 12, background: '#4ade80', border: 'none', color: '#08060f', fontWeight: 800, fontSize: 15, cursor: 'pointer', textAlign: 'center' },
    backBtn: { padding: '11px 24px', borderRadius: 10, background: '#1a0a3a', border: '1px solid #4c1d95', color: '#a78bfa', fontWeight: 600, fontSize: 14, cursor: 'pointer' },

    // Misc
    bigLabel: { fontSize: 22, fontWeight: 800, textAlign: 'center' },
    subLabel: { fontSize: 14, color: '#7c6fad', textAlign: 'center', lineHeight: 1.5 },
    errorBig: { fontSize: 18, color: '#f87171' },
    loadText: { color: '#7c6fad', fontSize: 14 },
    spinner: { width: 36, height: 36, borderRadius: '50%', border: '3px solid #2a1a4a', borderTop: '3px solid #7c3aed' },
}
